/**
 * Chiến lược chiếu sáng: mô tả ý định vận hành ("bật sau hoàng hôn 15 phút, 23h30 cắt bớt
 * ngõ ra 2") rồi để server sinh ra 48 timer cụ thể cho TỪNG tủ theo toạ độ của tủ đó.
 *
 * Hai nút quan trọng:
 *  - “Xem trước”: xem lịch chiến lược sẽ tạo cho MỘT tủ, hiện lịch + ghi chú, KHÔNG đẩy xuống tủ.
 *    (TK-28: lee thấy chữ "Sinh thử lịch" tối nghĩa ⇒ nút nay ghi "Xem lịch sẽ áp"; KHÔNG ghi "Đồng bộ" vì nút không gửi gì xuống tủ.)
 *  - “Áp cho cả tuyến”: đẩy thật, và bắt buộc hiện bảng kết quả từng tủ (kể cả tủ bị chặn
 *    vì mất dữ liệu khi hạ v1) — không gộp thành một dòng "thành công".
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, Copy, Eye, Gauge, Moon, Pencil, Plus, Route as Route2, Send, Trash2, XCircle,
} from 'lucide-react';
import { api, errorMessage } from '../lib/api';
import { qk, useAudit, useStrategies, useTree } from '../lib/queries';
import { CoQuyen, useAuth, useQuyen } from '../lib/auth';
import { BangKhoaTuyen } from '../components/Khoa';
import { NHAN_CO_TUYEN } from '../lib/quyen';
import {
  Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBox, Modal, OSoLon, Spinner,
  TimeInput, WarningBanner, useToast,
} from '../components/ui';
import { ALL_DAYS, describeDays, fmtAgo, fmtDateTime, minToStr } from '../lib/format';
import {
  MODE_LABEL, STRATEGY_KIND_LABEL,
  type AuditRow, type Strategy, type StrategyApplyResult, type StrategyKind,
  type StrategyPreview, type StrategySpec, type TreeRoute,
} from '../lib/types';

const KINDS: StrategyKind[] = ['SEASON', 'SEASON_TRIM', 'FIXED_TIME', 'MIDNIGHT_DIM', 'CUSTOM'];

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-08` 11/09/2026 — CHIẾN LƯỢC v2 (mẫu `_BUS/28`).
 *
 * 🔴 THỨ TRANG NÀY CHƯA BAO GIỜ TRẢ LỜI ĐƯỢC, và là lý do thật của phiếu: *"chiến lược này đã
 *   áp cho tuyến nào, lúc nào, và kết quả ra sao"*. Bản cũ vẽ một danh sách thẻ giống hệt nhau
 *   — một chiến lược đang chạy trên 3 tuyến và một chiến lược chưa ai dùng trông y như nhau.
 *   Người vận hành muốn biết thì phải nhớ, và không ai nhớ.
 * ⇒ Ba thứ mới, tất cả đọc từ dữ liệu ĐÃ CÓ: nhãn "Đang áp dụng" + chip tuyến (`routeLinks`,
 *   máy chủ thêm ở `GD-08`), bốn ô số, và bảng lịch sử áp đọc `AuditLog`.
 *
 * ⛔ VÀ CỘT "KẾT QUẢ" CỦA BẢNG LỊCH SỬ LÀ CHỖ ĐẮT NHẤT CỦA CẢ PHIẾU. Phiếu nói thẳng: KHÔNG
 *   ghi chung "Thành công". Một lần áp cho 8 tủ mà 1 tủ nhận và 7 tủ bị chặn vì mất dữ liệu
 *   khi hạ v1 **không phải là một thành công**, và gộp nó thành một chữ xanh là giấu đúng cái
 *   người ta cần biết — rồi họ sẽ đi tìm lỗi ở firmware vừa nạp.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/** Màu theo LOẠI chiến lược — đây là phân loại, KHÔNG phải trạng thái (luật màu `[95]`). */
const MAU_KIND: Record<StrategyKind, 'blue' | 'green' | 'amber' | 'slate'> = {
  SEASON: 'blue', SEASON_TRIM: 'green', FIXED_TIME: 'amber', MIDNIGHT_DIM: 'blue', CUSTOM: 'slate',
};

/**
 * ⛔ ĐANG ÁP = CÓ ÍT NHẤT MỘT `routeLinks[].appliedAt` KHÁC `null` — không phải "có routeLinks".
 *   Một hàng `RouteStrategy` tạo ra mà chưa áp lần nào là *"đã gắn, chưa áp"*; đếm nó vào
 *   "đang áp dụng" là khoe một lần áp chưa bao giờ xảy ra.
 * 🔴 TRẢ `null` KHI MÁY CHỦ KHÔNG NÓI (`routeLinks === undefined`, bản máy chủ cũ) — KHÔNG trả
 *   `false`. `false` là một lời khai *"tôi đã hỏi và câu trả lời là không"*, mà ở đây ta chưa
 *   hỏi được ai.
 */
function dangAp(s: Strategy): boolean | null {
  if (s.routeLinks === undefined) return null;
  return s.routeLinks.some((l) => l.appliedAt !== null);
}

/** Số tuyến ĐÃ ÁP THẬT của một chiến lược. `null` = máy chủ không nói. */
function soTuyenDaAp(s: Strategy): number | null {
  if (s.routeLinks === undefined) return null;
  return s.routeLinks.filter((l) => l.appliedAt !== null).length;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * "GIỜ CẮT/GIẢM MỖI ĐÊM" — một ƯỚC TÍNH, và nó phải tự khai ra điều đó.
 *
 * 🔴 PHIẾU CẤM HIỆN "% TIẾT KIỆM", và cái cấm ấy đúng vì một lý do đo được: app KHÔNG có số
 *   điện thật. Một con số "% tiết kiệm" dựng từ cấu hình trông y như một phép đo công tơ, và
 *   người đọc sẽ mang nó đi báo cáo. Số phút cắt/giảm thì khác — nó là thứ chính cấu hình
 *   NÓI RA, không phải thứ ta suy ra về thế giới.
 * ⚠ `dimPercent` là **MỨC SÁNG còn lại** (ô "Mức sáng (%)" trong trình soạn), nên phần giảm
 *   là `100 - dimPercent`. Đọc ngược dấu ở đây thì một chiến lược giảm còn 10% sẽ được khoe
 *   là tiết kiệm 10% thay vì 90%.
 * ⛔ CHỈ CỘNG CHIẾN LƯỢC ĐANG ÁP THẬT: cộng cả những cái chưa áp là đếm một thứ không xảy ra
 *   ngoài hiện trường.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function phutMoiDem(s: Strategy): number {
  const qua = (tu: number, den: number) => (den >= tu ? den - tu : 1440 - tu + den);
  let p = 0;
  if (s.kind === 'SEASON_TRIM' && s.spec.trimFromMin != null && s.spec.trimToMin != null) {
    p += qua(s.spec.trimFromMin, s.spec.trimToMin);
  }
  if (s.kind === 'MIDNIGHT_DIM' && s.spec.dimFromMin != null && s.spec.dimToMin != null) {
    const mucSang = s.spec.dimPercent ?? 100;
    p += qua(s.spec.dimFromMin, s.spec.dimToMin) * ((100 - mucSang) / 100);
  }
  return p;
}

/** TK-39 §1: `nhung` ⇒ khối "Sinh từ mẫu…" trong tab Đóng cắt của /cai-dat-chien-luoc — không tiêu đề trang, đủ chức năng. */
export default function StrategiesPage({ nhung = false }: { nhung?: boolean } = {}) {
  const strategies = useStrategies();
  const tree = useTree();
  /* `B4` mục 3: chiến lược đi bằng TICK `chien_luoc`, không bằng hạng — một khách có thể được
   cài chiến lược mà không được sửa cấu trúc tủ, thang hạng không nói được điều đó. */
  const canEdit = useQuyen('chien_luoc');
  /*
   * ⚠ `GD-08` mục 5 — LỊCH SỬ ÁP đọc `AuditLog`, hành động `APPLY_STRATEGY`. Máy chủ ĐÃ ghi
   *   dòng ấy từ trước; `GD-08` chỉ làm nó mang đủ ba con số kết quả (xem `api/schedule.ts`).
   * ⛔ Và KHÔNG gọi thêm hook nào khi không có quyền xem nhật ký — `useAudit` sẽ 403, và một
   *   ô đỏ trên trang Chiến lược nói về một cái quyền chẳng liên quan gì tới chiến lược.
   */
  const xemNhatKy = useQuyen('tao_tai_khoan');
  const lichSuAp = useAudit('APPLY_STRATEGY', 10, xemNhatKy);
  const toast = useToast();
  const qc = useQueryClient();

  const [editing, setEditing] = useState<Strategy | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Strategy | null>(null);
  const [previewFor, setPreviewFor] = useState<Strategy | null>(null);
  const [applyFor, setApplyFor] = useState<Strategy | null>(null);
  const [loc, setLoc] = useState<'tat-ca' | 'dang-ap' | StrategyKind>('tat-ca');

  useEffect(() => { if (!nhung) document.title = 'Chiến lược — SDKD'; }, [nhung]);

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/strategies/${id}`),
    onSuccess: () => {
      toast.success('Đã xoá chiến lược');
      setDeleting(null);
      qc.invalidateQueries({ queryKey: qk.strategies });
    },
    onError: (e) => toast.error('Không xoá được chiến lược', errorMessage(e)),
  });

  /*
   * ➕ NHÂN BẢN (phiếu mục 4) — dùng ĐÚNG `POST /api/strategies` đang có, không API mới.
   *
   * ⛔ TÊN BẢN SAO PHẢI KHÁC, và khác NHÌN THẤY ĐƯỢC: hai thẻ cùng tên trên cùng một lưới là
   *   chỗ người ta áp nhầm cái chưa sửa cho cả một tuyến. Hậu quả nằm ngoài màn hình.
   * ⚠ `spec` chép NGUYÊN — nhân bản để sửa một hai thông số; "dọn" giúp vài trường ở đây là
   *   đưa cho người dùng một bản sao không phải bản sao.
   */
  const nhanBan = useMutation({
    mutationFn: (s: Strategy) => api.post<Strategy>('/api/strategies', {
      name: `${s.name} (bản sao)`,
      kind: s.kind,
      description: s.description ?? '',
      spec: s.spec,
    }),
    onSuccess: (s) => {
      toast.success('Đã nhân bản chiến lược', `Bản mới: “${s.name}” — chưa áp cho tuyến nào.`);
      qc.invalidateQueries({ queryKey: qk.strategies });
    },
    onError: (e) => toast.error('Không nhân bản được', errorMessage(e)),
  });

  /*
   * ⛔ BỐN Ô SỐ ĐẾM TRÊN TOÀN BỘ, KHÔNG TRÊN PHẦN ĐANG LỌC — cùng cái bẫy đã cắt ở `GD-05`
   *   và `GD-06`: bấm một nút lọc rồi thấy "Tổng chiến lược 1" thì người ta tưởng vừa mất bốn.
   */
  const soLieu = useMemo(() => {
    const ds = strategies.data;
    if (!ds) return null;
    const ap = ds.filter((s) => dangAp(s) === true);
    /* Tuyến ĐÃ ÁP, đếm DUY NHẤT: một tuyến áp hai chiến lược vẫn là MỘT tuyến. */
    const tuyenDaAp = new Set(
      ds.flatMap((s) => (s.routeLinks ?? []).filter((l) => l.appliedAt !== null).map((l) => l.routeId)),
    );
    /* Máy chủ không nói `routeLinks` ⇒ ba ô dưới KHÔNG đo được, trả `null` chứ không trả 0. */
    const mayChuNoi = ds.every((s) => s.routeLinks !== undefined);
    return {
      tong: ds.length,
      dangAp: mayChuNoi ? ap.length : null,
      tuyenDaAp: mayChuNoi ? tuyenDaAp.size : null,
      tongTuyen: tree.data ? tree.data.length : null,
      phutDem: mayChuNoi ? ap.reduce((n, s) => n + phutMoiDem(s), 0) : null,
    };
  }, [strategies.data, tree.data]);

  const demTheoKind = useMemo(() => {
    const m = new Map<StrategyKind, number>();
    for (const s of strategies.data ?? []) m.set(s.kind, (m.get(s.kind) ?? 0) + 1);
    return m;
  }, [strategies.data]);

  const dsHien = useMemo(() => {
    const ds = strategies.data ?? [];
    if (loc === 'tat-ca') return ds;
    if (loc === 'dang-ap') return ds.filter((s) => dangAp(s) === true);
    return ds.filter((s) => s.kind === loc);
  }, [strategies.data, loc]);

  return (
    <div className="space-y-4">
      {/* ══════════ ① TIÊU ĐỀ ══════════ */}
      <div className="flex flex-wrap items-end gap-3">
        {!nhung && (<>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-nut bg-nhan/15 text-nhan">
          <Gauge className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h1 className="chu-tieude m-0 text-[26px] font-semibold leading-tight text-chu-1">
            Chiến lược chiếu sáng
          </h1>
          <p className="mt-0.5 text-[12.5px] text-chu-2">
            Mô tả ý định vận hành một lần, để máy chủ sinh 48 timer cho từng tủ theo toạ độ của nó
          </p>
        </div>
        </>)}
        <CoQuyen khoa="chien_luoc">
          <Button className="ml-auto" variant="primary" icon={<Plus className="h-4 w-4" />}
                  onClick={() => setEditing('new')}>
            Tạo chiến lược
          </Button>
        </CoQuyen>
      </div>

      {/* ══════════ ② BỐN Ô SỐ ══════════ */}
      <div data-vung="so-chien-luoc" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <OSoLon icon={<Gauge className="h-5 w-5" />} mau="nhan" nhan="Tổng chiến lược"
                tri={soLieu ? soLieu.tong : null}
                phu={soLieu ? `${demTheoKind.size} loại đang dùng` : 'chưa đọc được danh sách'} />
        {/*
          * ⛔ "CHƯA ÁP TUYẾN NÀO" ≠ "ĐANG ÁP 0" (phiếu ghi thẳng luật này). Ô số vẽ con số, và
          *   dòng phụ nói ra nghĩa của số 0 ấy — không để người đọc tự đoán.
          */}
        <OSoLon icon={<CheckCircle2 className="h-5 w-5" />} mau="tot" nhan="Đang áp dụng"
                tri={soLieu?.dangAp ?? null}
                phu={soLieu?.dangAp == null ? 'máy chủ chưa trả danh sách tuyến đã áp'
                  : soLieu.dangAp === 0 ? 'chưa chiến lược nào được áp cho tuyến nào'
                  : `${soLieu.tong - soLieu.dangAp} chiến lược chưa áp tuyến nào`} />
        <OSoLon icon={<Route2 className="h-5 w-5" />} mau="canh" nhan="Tuyến đã áp"
                tri={soLieu?.tuyenDaAp == null || soLieu.tongTuyen == null
                  ? null : `${soLieu.tuyenDaAp} / ${soLieu.tongTuyen}`}
                phu={soLieu?.tongTuyen == null ? 'chưa đọc được cây tuyến' : 'trên tổng số tuyến đang xem'} />
        {/*
          * 🔴 KHÔNG CÓ "% TIẾT KIỆM" (phiếu cấm). App không có số điện thật; một con số phần
          *   trăm dựng từ cấu hình trông y như một phép đo công tơ, và nó sẽ đi vào báo cáo.
          */}
        <OSoLon icon={<Moon className="h-5 w-5" />} mau="tim" nhan="Giờ cắt/giảm mỗi đêm"
                tri={soLieu?.phutDem == null ? null : (soLieu.phutDem / 60).toFixed(1)}
                donVi="giờ"
                phu={soLieu?.phutDem == null
                  ? 'chưa đo được'
                  : 'ước tính theo cấu hình, CHƯA đo bằng công tơ'} />
      </div>

      {/* ══════════ ③ NÚT LỌC ══════════ */}
      {/*
        * ⚠ MỖI NÚT MANG SỐ CỦA CHÍNH NÓ, kể cả khi số ấy là 0 — một nút lọc không có số là một
        *   nút người ta phải bấm mới biết có gì phía sau, và bấm vào một danh sách rỗng ba lần
        *   là lần thứ tư thôi bấm.
        */}
      <div className="flex flex-wrap items-center gap-2">
        <NutLoc dang={loc === 'tat-ca'} onBam={() => setLoc('tat-ca')} so={strategies.data?.length ?? null}>
          Tất cả
        </NutLoc>
        <NutLoc dang={loc === 'dang-ap'} onBam={() => setLoc('dang-ap')} so={soLieu?.dangAp ?? null}>
          Đang áp dụng
        </NutLoc>
        {KINDS.filter((k) => (demTheoKind.get(k) ?? 0) > 0).map((k) => (
          <NutLoc key={k} dang={loc === k} onBam={() => setLoc(k)} so={demTheoKind.get(k) ?? 0}>
            {STRATEGY_KIND_LABEL[k]}
          </NutLoc>
        ))}
      </div>

      {strategies.isLoading && <Spinner label="Đang tải danh sách chiến lược…" />}
      {strategies.isError && <ErrorBox error={strategies.error} onRetry={() => void strategies.refetch()} />}

      {strategies.data && strategies.data.length === 0 && (
        <Card>
          <EmptyState
            icon={<Gauge className="h-8 w-8" />}
            title="Chưa có chiến lược nào"
            hint="Chiến lược giúp áp cùng một quy tắc chiếu sáng cho nhiều tủ mà không phải soạn tay 48 timer từng tủ."
            action={canEdit ? <Button className="mt-2" variant="primary" onClick={() => setEditing('new')}>Tạo chiến lược đầu tiên</Button> : undefined}
          />
        </Card>
      )}

      {/* ⚠ RỖNG VÌ LỌC ≠ RỖNG VÌ CHƯA CÓ — hai ca, hai câu (cùng luật `GD-05`). */}
      {strategies.data && strategies.data.length > 0 && dsHien.length === 0 && (
        <Card>
          <EmptyState
            icon={<Gauge className="h-8 w-8" />}
            title="Không chiến lược nào khớp bộ lọc"
            hint={`Bộ lọc chỉ giấu bớt — ${strategies.data.length} chiến lược vẫn còn nguyên.`}
          />
        </Card>
      )}

      {/* ══════════ ④ THẺ CHIẾN LƯỢC ══════════ */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {dsHien.map((s) => {
          const ap = dangAp(s);
          const soTuyen = soTuyenDaAp(s);
          return (
            <Card
              key={s.id}
              title={s.name}
              subtitle={STRATEGY_KIND_LABEL[s.kind]}
              actions={
                <>
                  <Button size="sm" icon={<Eye className="h-4 w-4" />} onClick={() => setPreviewFor(s)}>Xem trước</Button>
                  <CoQuyen khoa="chien_luoc">
                    <Button size="sm" variant="primary" icon={<Send className="h-4 w-4" />} onClick={() => setApplyFor(s)}>
                      Áp cho cả tuyến
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditing(s)}>
                      <span className="sr-only">Sửa</span>
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Copy className="h-4 w-4" />}
                            loading={nhanBan.isPending && nhanBan.variables?.id === s.id}
                            onClick={() => nhanBan.mutate(s)} title="Nhân bản để sửa thành một bản khác">
                      <span className="sr-only">Nhân bản</span>
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setDeleting(s)}>
                      <span className="sr-only">Xoá</span>
                    </Button>
                  </CoQuyen>
                </>
              }
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge tone={MAU_KIND[s.kind]}>{STRATEGY_KIND_LABEL[s.kind]}</Badge>
                {/*
                  * 🔴 BA CA, BA CÂU (Luật số 0):
                  *     `null`  — máy chủ không nói được tuyến nào ⇒ im lặng về chuyện áp
                  *     `false` — máy chủ nói: CHƯA áp tuyến nào
                  *     `true`  — đang áp, kèm số tuyến
                  *   Gộp `null` vào `false` là khai một điều chưa hỏi được ai.
                  */}
                {ap === null ? (
                  <Badge tone="slate">chưa đo được tuyến đã áp</Badge>
                ) : ap ? (
                  <Badge tone="green"><CheckCircle2 className="h-3 w-3" />Đang áp dụng · {soTuyen} tuyến</Badge>
                ) : (
                  <Badge tone="slate">chưa áp tuyến nào</Badge>
                )}
              </div>

              {s.description && <p className="mb-2 text-sm text-chu-2">{s.description}</p>}
              <SpecSummary kind={s.kind} spec={s.spec} />

              {/*
                * ⛔ CHIP TUYẾN + THỜI ĐIỂM. Tuyến ĐÃ GẮN mà CHƯA ÁP cũng hiện — xám, và nói ra
                *   là "chưa áp lần nào". Giấu nó đi thì một tuyến đã gắn nhưng lần áp rớt giữa
                *   chừng sẽ biến mất khỏi màn hình, và không ai đi áp lại.
                */}
              {s.routeLinks && s.routeLinks.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 border-t border-vien pt-2">
                  {s.routeLinks.map((l) => (
                    <Badge key={l.routeId} tone={l.appliedAt ? 'blue' : 'slate'}>
                      {l.tenDayDu}
                      <span className="ml-1 text-xs opacity-80">
                        {l.appliedAt ? fmtAgo(l.appliedAt) : 'chưa áp lần nào'}
                      </span>
                    </Badge>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* ══════════ ⑤ LỊCH SỬ ÁP DỤNG GẦN ĐÂY ══════════ */}
      {/*
        * ⛔ KHÔNG DỰNG KHỐI KHI KHÔNG CÓ QUYỀN XEM NHẬT KÝ — một bảng rỗng ở đây đọc thành
        *   "chưa ai áp chiến lược nào", một câu SAI với đúng người đọc nó (cùng luật `Q14`).
        */}
      {xemNhatKy && (
        <Card
          title="Lịch sử áp dụng gần đây"
          subtitle="10 lần áp gần nhất — đọc từ nhật ký thao tác"
          actions={
            <Link to="/nhat-ky?action=APPLY_STRATEGY" className="text-sm text-nhan hover:underline">
              Xem tất cả →
            </Link>
          }
        >
          {lichSuAp.isLoading && <Spinner />}
          {lichSuAp.isError && <ErrorBox error={lichSuAp.error} onRetry={() => void lichSuAp.refetch()} />}
          {lichSuAp.data && lichSuAp.data.length === 0 && (
            <EmptyState icon={<Send className="h-8 w-8" />} title="Chưa có lần áp chiến lược nào" />
          )}
          {lichSuAp.data && lichSuAp.data.length > 0 && (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Thời điểm</th>
                    <th>Chiến lược</th>
                    <th>Tuyến</th>
                    <th>Người áp</th>
                    <th>Kết quả</th>
                  </tr>
                </thead>
                <tbody>
                  {lichSuAp.data.map((h) => (
                    <HangLichSuAp key={h.id} hang={h} chienLuocs={strategies.data ?? []} tuyens={tree.data ?? []} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {editing && (
        <StrategyEditor
          strategy={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {previewFor && (
        <PreviewDialog strategy={previewFor} onClose={() => setPreviewFor(null)} />
      )}

      {applyFor && (
        <ApplyDialog strategy={applyFor} onClose={() => setApplyFor(null)} />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Xoá chiến lược"
        message={
          <>
            Xoá chiến lược <b>{deleting?.name}</b>? Lịch đã đẩy xuống tủ trước đó KHÔNG bị ảnh hưởng,
            nhưng bạn sẽ không áp lại được chiến lược này nữa.
          </>
        }
        confirmText="Xoá"
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />

      {tree.isError && <ErrorBox error={tree.error} />}
    </div>
  );
}

/** Một nút lọc mang sẵn con số của chính nó. `so === null` ⇒ chưa đo được, in `—`. */
function NutLoc({ dang, so, onBam, children }: {
  dang: boolean; so: number | null; onBam: () => void; children: ReactNode;
}) {
  return (
    <button type="button" onClick={onBam}
      className={`rounded-full border px-3 py-1 text-xs transition ${
        dang ? 'border-nhan/50 bg-nhan/15 text-nhan' : 'border-vien text-chu-2 hover:bg-nen-2'}`}>
      {children}
      <span className="ml-1.5 text-chu-3">{so === null ? '—' : so}</span>
    </button>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * MỘT DÒNG LỊCH SỬ ÁP — và cột "Kết quả" là chỗ phiếu `GD-08` nói thẳng: **KHÔNG ghi chung
 * "Thành công"**.
 *
 * 🔴 Một lần áp cho 8 tủ mà 1 tủ nhận, 7 tủ bị chặn vì mất dữ liệu khi hạ v1 KHÔNG phải một
 *   thành công. Gộp nó thành một chữ xanh là giấu đúng cái người ta cần biết — và họ sẽ đi
 *   tìm lỗi ở firmware vừa nạp, đúng cái giá dự án này đã trả nhiều lần.
 *
 * ⛔ DÒNG NHẬT KÝ CŨ KHÔNG CÓ BA CON SỐ ẤY. `GD-08` mới thêm `nhan`/`chan`/`matMat` vào dòng
 *   ghi; mọi lần áp TRƯỚC hôm nay chỉ có `count`. ⇒ Ca ấy phải NÓI RA — *"bản máy chủ cũ
 *   không ghi số"* — chứ KHÔNG vẽ `0/n tủ nhận`, vì `0 tủ nhận` là một câu về hiện trường, và
 *   nó sẽ khiến người đọc tưởng một lần áp thành công là một lần hỏng.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function HangLichSuAp({ hang, chienLuocs, tuyens }: {
  hang: AuditRow; chienLuocs: Strategy[]; tuyens: TreeRoute[];
}) {
  const d = (hang.detail ?? {}) as {
    count?: number; routeId?: string; tong?: number; nhan?: number; chan?: number; matMat?: string[];
  };
  /* `target` dạng `strategy:<id>` — tra tên; tra KHÔNG ra thì in `id`, không im lặng bỏ dòng. */
  const clId = (hang.target ?? '').startsWith('strategy:') ? hang.target!.slice('strategy:'.length) : null;
  const cl = clId ? chienLuocs.find((s) => s.id === clId) ?? null : null;
  const tuyen = d.routeId ? tuyens.find((r) => r.id === d.routeId) ?? null : null;
  /* ⛔ CÓ ĐỦ BA CON SỐ hay không — đo bằng `nhan`, thứ chỉ bản ghi MỚI mang. */
  const coSo = typeof d.nhan === 'number' && typeof d.tong === 'number';

  return (
    <tr>
      <td className="whitespace-nowrap text-chu-2">{fmtDateTime(hang.at)}</td>
      <td className="text-chu-1">{cl?.name ?? (clId ? <span className="so-do text-xs">{clId}</span> : <span className="text-chu-3">—</span>)}</td>
      {/*
        * ⚠ TUYẾN TRA KHÔNG RA TÊN ⇒ IN `id`, không bỏ dòng (cùng luật `GD-06`): một tuyến đã bị
        *   xoá vẫn là một lần áp ĐÃ XẢY RA, và giấu nó đi là sửa lại lịch sử.
        */}
      <td className="text-chu-2">
        {tuyen?.tenDayDu ?? (d.routeId ? <span className="so-do text-xs">{d.routeId}</span> : <span className="text-chu-3">cả tủ lẻ</span>)}
      </td>
      <td className="whitespace-nowrap text-chu-2">
        {hang.user ? `@${hang.user.username}` : <span className="text-chu-3">không rõ</span>}
      </td>
      <td>
        {!coSo ? (
          <span className="text-chua-ro">
            bản máy chủ cũ không ghi số
            {typeof d.count === 'number' && <span className="ml-1 text-chu-3">(đã thử {d.count} tủ)</span>}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge tone={d.nhan === d.tong ? 'green' : d.nhan === 0 ? 'red' : 'amber'}>
              {d.nhan}/{d.tong} tủ nhận
            </Badge>
            {(d.chan ?? 0) > 0 && <Badge tone="amber"><XCircle className="h-3 w-3" />{d.chan} chặn</Badge>}
            {(d.matMat?.length ?? 0) > 0 && (
              <span className="text-xs text-canh" title={(d.matMat ?? []).join(' · ')}>
                mất mát: {(d.matMat ?? []).join(' · ')}
              </span>
            )}
          </span>
        )}
      </td>
    </tr>
  );
}

/* ---------------------------------------------------------- tóm tắt spec */

function SpecSummary({ kind, spec }: { kind: StrategyKind; spec: StrategySpec }) {
  const outs = spec.outputs?.length ? spec.outputs : [1, 2, 3];
  const rows: { label: string; value: string }[] = [
    { label: 'Ngõ ra áp dụng', value: outs.join(', ') },
  ];

  if (kind === 'FIXED_TIME') {
    rows.push({
      label: 'Khung giờ',
      value: (spec.windows ?? []).length
        ? (spec.windows ?? [])
          .map((w) => `${minToStr(w.onMin)}–${minToStr(w.offMin)} (${describeDays(w.daysOfWeek ?? ALL_DAYS)})`)
          .join(' · ')
        : 'chưa khai báo khung giờ nào',
    });
  } else {
    rows.push({ label: 'Lệch hoàng hôn', value: `${spec.sunsetOffsetMin ?? 0} phút` });
    rows.push({ label: 'Lệch bình minh', value: `${spec.sunriseOffsetMin ?? 0} phút` });
  }

  if (kind === 'SEASON_TRIM') {
    rows.push({
      label: 'Cắt bớt giữa đêm',
      value: `${minToStr(spec.trimFromMin ?? 23 * 60 + 30)}–${minToStr(spec.trimToMin ?? 4 * 60 + 30)} cho ngõ ra ${(spec.trimOutputs?.length ? spec.trimOutputs : [2]).join(', ')}`,
    });
  }

  if (kind === 'MIDNIGHT_DIM') {
    rows.push({
      label: 'Giảm sáng',
      value: `${spec.dimPercent ?? 50}% từ ${minToStr(spec.dimFromMin ?? 0)} đến ${minToStr(spec.dimToMin ?? 0)}`,
    });
  }

  if (spec.specialDays?.length) {
    rows.push({
      label: 'Ngày đặc biệt',
      value: spec.specialDays.map((d) => `${String(d.d).padStart(2, '0')}/${String(d.m).padStart(2, '0')}`).join(', '),
    });
  }

  return (
    <dl className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
      {rows.map((r) => (
        <div key={r.label} className="flex gap-1">
          <dt className="text-chu-3">{r.label}:</dt>
          <dd className="font-medium text-chu-1">{r.value}</dd>
        </div>
      ))}
      {kind === 'MIDNIGHT_DIM' && (
        <p className="col-span-full mt-1 text-xs text-canh">
          Lưu ý: firmware hiện tại chưa xử lý lệnh giảm sáng — chỉ tủ có báo hỗ trợ dim mới dùng được.
        </p>
      )}
    </dl>
  );
}

/* --------------------------------------------------------- soạn chiến lược */

function StrategyEditor({ strategy, onClose }: { strategy: Strategy | null; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const isNew = !strategy;

  const [name, setName] = useState(strategy?.name ?? '');
  const [kind, setKind] = useState<StrategyKind>(strategy?.kind ?? 'SEASON');
  const [description, setDescription] = useState(strategy?.description ?? '');
  const [spec, setSpec] = useState<StrategySpec>(() => ({
    outputs: [1, 2, 3],
    sunsetOffsetMin: 0,
    sunriseOffsetMin: 0,
    ...(strategy?.spec ?? {}),
  }));

  const patch = (p: Partial<StrategySpec>) => setSpec((prev) => ({ ...prev, ...p }));

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), kind, description: description.trim(), spec };
      return isNew
        ? api.post<Strategy>('/api/strategies', body)
        : api.patch<Strategy>(`/api/strategies/${strategy!.id}`, body);
    },
    onSuccess: () => {
      toast.success(isNew ? 'Đã tạo chiến lược' : 'Đã cập nhật chiến lược');
      qc.invalidateQueries({ queryKey: qk.strategies });
      onClose();
    },
    onError: (e) => toast.error('Không lưu được chiến lược', errorMessage(e)),
  });

  const outs = spec.outputs ?? [1, 2, 3];
  const toggleOut = (i: number, list: number[], key: 'outputs' | 'trimOutputs') => {
    const next = list.includes(i) ? list.filter((x) => x !== i) : [...list, i].sort();
    patch(key === 'outputs' ? { outputs: next } : { trimOutputs: next });
  };

  return (
    <Modal
      open
      onClose={onClose}
      wide
      title={isNew ? 'Tạo chiến lược mới' : `Sửa chiến lược: ${strategy!.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>Huỷ</Button>
          <Button variant="primary" loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
            {isNew ? 'Tạo' : 'Lưu thay đổi'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="lbl">Tên chiến lược</span>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="vd: Mùa hè — tuyến trung tâm" />
          </label>
          <label className="block">
            <span className="lbl">Kiểu chiến lược</span>
            <select className="inp" value={kind} onChange={(e) => setKind(e.target.value as StrategyKind)}>
              {KINDS.map((k) => <option key={k} value={k}>{STRATEGY_KIND_LABEL[k]}</option>)}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="lbl">Mô tả</span>
          <textarea
            className="inp min-h-[72px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ghi rõ áp dụng cho tuyến nào, giai đoạn nào, ai duyệt…"
          />
        </label>

        <div>
          <span className="lbl">Ngõ ra áp dụng</span>
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleOut(i, outs, 'outputs')}
                className={[
                  'rounded-lg border px-4 py-2 text-sm font-medium transition',
                  outs.includes(i) ? 'border-nhan bg-nhan/15 text-nhan' : 'border-vien text-chu-2',
                ].join(' ')}
              >
                Ngõ ra {i}
              </button>
            ))}
          </div>
        </div>

        {kind === 'FIXED_TIME' ? (
          <FixedWindowsEditor spec={spec} patch={patch} />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="lbl">Bật sau hoàng hôn (phút, số âm = bật sớm hơn)</span>
              <input
                type="number" min={-180} max={180} className="inp"
                value={spec.sunsetOffsetMin ?? 0}
                onChange={(e) => patch({ sunsetOffsetMin: clamp180(e.target.value) })}
              />
            </label>
            <label className="block">
              <span className="lbl">Tắt sau bình minh (phút, số âm = tắt sớm hơn)</span>
              <input
                type="number" min={-180} max={180} className="inp"
                value={spec.sunriseOffsetMin ?? 0}
                onChange={(e) => patch({ sunriseOffsetMin: clamp180(e.target.value) })}
              />
            </label>
          </div>
        )}

        {kind === 'SEASON_TRIM' && (
          <div className="rounded-lg border border-vien p-3">
            <p className="mb-2 text-sm font-semibold text-chu-1">Cắt bớt giữa đêm</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="lbl">Bắt đầu cắt</span>
                <TimeInput value={spec.trimFromMin ?? 23 * 60 + 30} onChange={(m) => patch({ trimFromMin: m })} />
              </label>
              <label className="block">
                <span className="lbl">Kết thúc cắt</span>
                <TimeInput value={spec.trimToMin ?? 4 * 60 + 30} onChange={(m) => patch({ trimToMin: m })} />
              </label>
              <div>
                <span className="lbl">Ngõ ra bị cắt</span>
                <div className="flex gap-1">
                  {[1, 2, 3].map((i) => {
                    const list = spec.trimOutputs?.length ? spec.trimOutputs : [2];
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggleOut(i, list, 'trimOutputs')}
                        className={[
                          'rounded-lg border px-3 py-2 text-sm transition',
                          list.includes(i) ? 'border-canh bg-canh-nen text-canh' : 'border-vien text-chu-2',
                        ].join(' ')}
                      >
                        {i}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {kind === 'MIDNIGHT_DIM' && (
          <div className="rounded-lg border border-vien p-3">
            <p className="mb-2 text-sm font-semibold text-chu-1">Giảm sáng khuya</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="lbl">Từ</span>
                <TimeInput value={spec.dimFromMin ?? 23 * 60} onChange={(m) => patch({ dimFromMin: m })} />
              </label>
              <label className="block">
                <span className="lbl">Đến</span>
                <TimeInput value={spec.dimToMin ?? 4 * 60} onChange={(m) => patch({ dimToMin: m })} />
              </label>
              <label className="block">
                <span className="lbl">Mức sáng (%)</span>
                <input
                  type="number" min={0} max={100} className="inp"
                  value={spec.dimPercent ?? 50}
                  onChange={(e) => patch({ dimPercent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-canh">
              Chỉ tủ báo có hỗ trợ dim mới thực thi được; các tủ khác vẫn nhận phần timer bật/tắt.
            </p>
          </div>
        )}

        {kind === 'CUSTOM' && (
          <WarningBanner tone="blue" title="Chiến lược tự định nghĩa">
            <p className="mt-1 text-xs">
              Server không sinh timer tự động cho kiểu CUSTOM — hãy soạn lịch trực tiếp ở màn hình
              “Lịch đóng cắt” của từng tủ.
            </p>
          </WarningBanner>
        )}
      </div>
    </Modal>
  );
}

function clamp180(v: string): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-180, Math.min(180, Math.round(n)));
}

function FixedWindowsEditor({
  spec, patch,
}: { spec: StrategySpec; patch: (p: Partial<StrategySpec>) => void }) {
  const windows = spec.windows ?? [];

  const setWindow = (i: number, p: Partial<{ onMin: number; offMin: number; daysOfWeek: string }>) =>
    patch({ windows: windows.map((w, k) => (k === i ? { ...w, ...p } : w)) });

  return (
    <div className="rounded-lg border border-vien p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-chu-1">Khung giờ cố định</p>
        <Button
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => patch({ windows: [...windows, { onMin: 18 * 60, offMin: 5 * 60, daysOfWeek: ALL_DAYS }] })}
        >
          Thêm khung giờ
        </Button>
      </div>

      {windows.length === 0 && <p className="text-sm text-chu-2">Chưa có khung giờ nào.</p>}

      <div className="space-y-2">
        {windows.map((w, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-vien p-2">
            <span className="text-xs text-chu-3">#{i + 1}</span>
            <span className="text-xs text-chu-2">Bật</span>
            <TimeInput value={w.onMin} onChange={(m) => setWindow(i, { onMin: m })} className="w-28" />
            <span className="text-xs text-chu-2">Tắt</span>
            <TimeInput value={w.offMin} onChange={(m) => setWindow(i, { offMin: m })} className="w-28" />
            <span className="text-xs text-chu-2">{describeDays(w.daysOfWeek ?? ALL_DAYS)}</span>
            <Button
              size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />}
              onClick={() => patch({ windows: windows.filter((_, k) => k !== i) })}
              className="ml-auto"
            >
              <span className="sr-only">Xoá khung giờ</span>
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- xem trước */

function PreviewDialog({ strategy, onClose }: { strategy: Strategy; onClose: () => void }) {
  const tree = useTree();
  const toast = useToast();
  const cabinets = useMemo(
    () => (tree.data ?? []).flatMap((r) => r.cabinets.map((c) => ({ ...c, routeName: r.tenDayDu }))),
    [tree.data]
  );
  const [cabinetId, setCabinetId] = useState('');
  const [result, setResult] = useState<StrategyPreview | null>(null);

  const run = useMutation({
    mutationFn: () => api.post<StrategyPreview>(`/api/strategies/${strategy.id}/preview`, { cabinetId }),
    onSuccess: (res) => setResult(res),
    onError: (e) => toast.error('Không xem trước được', errorMessage(e)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      wide
      hoiKhiDong={false} /* TK-27: hộp CHỈ XEM — ô chọn tủ ở đây là để xem, không có gì để mất */
      title={`Xem trước: ${strategy.name}`}
      subtitle="Xem lịch mà chiến lược này sẽ tạo cho một tủ — chỉ để xem, tủ KHÔNG thay đổi gì"
      footer={<Button variant="secondary" onClick={onClose}>Đóng</Button>}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="block min-w-[16rem] flex-1">
            <span className="lbl">Chọn tủ để xem</span>
            <select className="inp" value={cabinetId} onChange={(e) => { setCabinetId(e.target.value); setResult(null); }}>
              <option value="">— Chọn tủ —</option>
              {cabinets.map((c) => (
                <option key={c.id} value={c.id}>{c.routeName} / {c.name} ({c.code})</option>
              ))}
            </select>
          </label>
          <Button variant="primary" disabled={!cabinetId} loading={run.isPending} onClick={() => run.mutate()}>
            Xem lịch sẽ áp
          </Button>
        </div>

        {result && (
          <>
            {result.notes.length > 0 && (
              <WarningBanner tone="blue" title="Ghi chú về lịch này" items={result.notes} />
            )}

            {result.v1Preview.losses.length > 0 && (
              <WarningBanner
                tone="amber"
                title={`${result.v1Preview.losses.length} mục sẽ mất nếu đẩy bằng giao thức v1`}
                items={result.v1Preview.losses}
              />
            )}

            <p className="text-xs text-chu-2">Số khe v1 sử dụng: {result.v1Preview.usedSlots}/48</p>

            <div className="space-y-3">
              {result.schedule.outputs.map((o) => {
                const timers = o.timers.filter((t) => t.enabled);
                return (
                  <div key={o.index} className="rounded-lg border border-vien p-3">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-chu-1">{o.name}</p>
                      <Badge tone="blue">{MODE_LABEL[o.mode]}</Badge>
                      {o.mode === 'SEASON' && (
                        <Badge tone="slate">
                          lệch bật {o.sunsetOffsetMin}′ · lệch tắt {o.sunriseOffsetMin}′
                        </Badge>
                      )}
                      {!o.enabled && <Badge tone="amber">bị khoá</Badge>}
                    </div>
                    {timers.length === 0 ? (
                      <p className="text-xs text-chu-2">Ngõ ra này không có giờ bật nào.</p>
                    ) : (
                      <ul className="space-y-1 text-sm">
                        {timers.map((t) => (
                          <li key={t.index} className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-chu-3">khe {t.index}</span>
                            <span className="font-medium text-chu-1">{minToStr(t.onMin)} → {minToStr(t.offMin)}</span>
                            <span className="text-xs text-chu-2">{describeDays(t.daysOfWeek)}</span>
                            {t.specialDays.length > 0 && (
                              <Badge tone="blue">{t.specialDays.length} ngày đặc biệt</Badge>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------- áp cho cả tuyến */

function ApplyDialog({ strategy, onClose }: { strategy: Strategy; onClose: () => void }) {
  const tree = useTree();
  const { coCoTuyen } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [routeId, setRouteId] = useState('');
  const [result, setResult] = useState<StrategyApplyResult | null>(null);

  const route = (tree.data ?? []).find((r) => r.id === routeId) ?? null;

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-09` mục 9 — CỔNG `canEditSchedule` **TRÊN ĐÚNG TUYẾN VỪA CHỌN**.
   *
   * 🔴 dev_device hỏi: *"`khach_hang_11` có tick `chien_luoc` nhưng KHÔNG có cờ đặt lịch trên
   *   HHG; bấm Áp cho cả tuyến HHG thì máy chủ có chặn không?"* — **KHÔNG**, và đó là một lỗ
   *   thật: `POST /api/strategies/:id/apply` chỉ đi qua `requireQuyen('chien_luoc')`, trong khi
   *   bốn đường ghi lịch khác đều qua `requireCoTuyen('canEditSchedule')`. Đã bịt ở máy chủ
   *   (`api/schedule.ts`) — đó mới là cổng thật.
   * ⛔ NÚT NÀY LÀ **CỬA LỊCH SỰ**, không phải cổng: nó chỉ để người dùng biết trước thay vì
   *   bấm rồi ăn 403. Bịt ở web mà quên máy chủ là đúng bệnh "nút gạt giả" của YC-50.
   * ⚠ ĐỌC CỜ CỦA TUYẾN ĐANG CHỌN, không phải "có tuyến nào đó cho phép": một khách có thể đặt
   *   lịch trên tuyến A mà không được trên tuyến B — đúng ca `A.3` sinh ra để diễn tả.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const qdLich = coCoTuyen('canEditSchedule', routeId || null);
  const duocAp = qdLich === 'cho-qua';

  const apply = useMutation({
    mutationFn: (acceptLosses: boolean) =>
      api.post<StrategyApplyResult>(`/api/strategies/${strategy.id}/apply`, { routeId, acceptLosses }),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: qk.tree });
      if (res.blocked > 0) {
        toast.warning(
          `${res.blocked}/${res.total} tủ bị CHẶN vì mất dữ liệu khi hạ v1`,
          'Xem danh sách mất mát trong bảng kết quả rồi quyết định có đẩy tiếp hay không.'
        );
      } else {
        toast.success(`Đã áp chiến lược cho ${res.applied}/${res.total} tủ`);
      }
    },
    onError: (e) => toast.error('Không áp được chiến lược', errorMessage(e)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      wide
      title={`Áp chiến lược cho cả tuyến: ${strategy.name}`}
      subtitle="Lịch sẽ được sinh riêng cho từng tủ theo toạ độ của tủ đó, rồi đẩy xuống ngay"
      footer={<Button variant="secondary" onClick={onClose}>Đóng</Button>}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="block min-w-[16rem] flex-1">
            <span className="lbl">Tuyến đường</span>
            <select className="inp" value={routeId} onChange={(e) => { setRouteId(e.target.value); setResult(null); }}>
              <option value="">— Chọn tuyến —</option>
              {(tree.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.tenDayDu} — {r.cabinetCount} tủ
                </option>
              ))}
            </select>
          </label>
          <Button
            variant="primary"
            icon={<Send className="h-4 w-4" />}
            disabled={!routeId || !duocAp}
            loading={apply.isPending && apply.variables === false}
            onClick={() => apply.mutate(false)}
          >
            Áp cho {route ? `${route.cabinetCount} tủ` : 'tuyến'}
          </Button>
        </div>

        {/*
          * ⚠ CHỌN RỒI MỚI NÓI ĐƯỢC: chưa chọn tuyến thì chưa có câu nào đúng để nói, nên băng
          *   chỉ hiện khi `routeId` đã có. `BangKhoaTuyen` tự im khi `'cho-qua'`, và tự vẽ xám
          *   khi `'chua-biet'` (chưa hỏi xong `/me`).
          * 🔴 VÀ NÓI RA VÌ SAO MỘT TICK KHÔNG ĐỦ: người dùng CÓ `chien_luoc`, nên "không có
          *   quyền" nghe như app hỏng. Câu phải nối được hai việc: áp chiến lược **là** đẩy
          *   lịch, nên nó cần cờ lịch của chính tuyến ấy.
          */}
        {routeId && (
          <>
            <BangKhoaTuyen qd={qdLich} co="canEditSchedule" />
            {qdLich !== 'cho-qua' && qdLich !== 'chua-biet' && (
              <p className="text-xs text-chu-2">
                Áp chiến lược <b>là đẩy lịch xuống tủ</b>, nên ngoài tính năng “Cài chiến lược”
                nó còn cần cờ “{NHAN_CO_TUYEN.canEditSchedule}” trên đúng tuyến này.
              </p>
            )}
          </>
        )}

        {result && (
          <>
            <div className="flex flex-wrap gap-1.5">
              <Badge tone="green"><CheckCircle2 className="h-3 w-3" />{result.applied} tủ đã áp</Badge>
              <Badge tone="amber">{result.blocked} tủ bị chặn</Badge>
              <Badge tone="red"><XCircle className="h-3 w-3" />{result.total - result.applied - result.blocked} tủ lỗi</Badge>
              <Badge tone="slate">{result.total} tổng cộng</Badge>
            </div>

            <div className="space-y-2">
              {result.results.map((r) => (
                <div
                  key={r.cabinetId}
                  className={[
                    'rounded-lg border p-3 text-sm',
                    r.ok ? 'border-tot/50 bg-tot/15' : r.blocked ? 'border-canh/50 bg-canh-nen' : 'border-nguy/50 bg-nguy-nen',
                  ].join(' ')}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/tu/${r.cabinetId}`} className="font-medium text-nhan hover:underline">
                      {r.code}
                    </Link>
                    {r.ok ? (
                      <Badge tone="green">Đã đẩy xuống tủ</Badge>
                    ) : r.blocked ? (
                      <Badge tone="amber">Bị chặn — sẽ mất dữ liệu khi hạ v1</Badge>
                    ) : (
                      <Badge tone="red">Lỗi: {r.error ?? 'không rõ'}</Badge>
                    )}
                  </div>

                  {r.blocked && r.losses && r.losses.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-canh">
                      {r.losses.map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                  )}
                  {r.notes && r.notes.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-chu-2">
                      {r.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {result.blocked > 0 && (
              <div className="rounded-lg border border-canh/50 bg-canh-nen p-3">
                <p className="text-sm font-semibold text-canh">
                  {result.blocked} tủ chưa nhận lịch vì giao thức v1 không chở hết dữ liệu
                </p>
                <p className="mt-1 text-xs text-canh">
                  Đọc kỹ danh sách mất mát ở trên. Nếu chấp nhận phần bị mất, bấm nút dưới đây để
                  đẩy tiếp phần truyền được cho những tủ đó.
                </p>
                <Button
                  className="mt-2"
                  variant="danger"
                  loading={apply.isPending && apply.variables === true}
                  onClick={() => apply.mutate(true)}
                >
                  Tôi hiểu, vẫn đẩy cho các tủ bị chặn
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
